import React, { useState, useEffect } from 'react';
import NoteEditor from './NoteEditor';
import { DocumentNotesApi } from '../../services/api/DocumentNotesApi';
import { useAuth } from '../../contexts/AuthContext';
import './DocumentNotes.css';

/**
 * DocumentNotes Component
 * Manages notes linked to a specific document (one note per document)
 * 
 * **Editor Implementation:**
 * - Uses NoteEditor which wraps CourseNoteRichEditor (TipTap WYSIWYG)
 * - Same toolbar and formatting as Course Notes (B/I/U/Highlight/H1-H3/lists/etc.)
 * - Stores Markdown in backend, displays as rich text
 * - Same "saved/saving/unsaved" status behavior
 * 
 * **Single Note Per Document:**
 * - Auto-creates note on first edit if it doesn't exist
 * - No note list UI - always shows editor directly
 * - Simplified header (no back button, no rename)
 * 
 * @param {Object} props
 * @param {number} props.documentId - Parent document ID
 * @param {string} props.documentName - Parent document name
 */
const DocumentNotes = ({ documentId, documentName }) => {
  const { user } = useAuth();
  const [noteDocument, setNoteDocument] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  // Load existing note or prepare for creation
  useEffect(() => {
    const loadNote = async () => {
      if (!documentId || !user?.userId) return;

      setLoading(true);
      setError(null);

      try {
        // Check if a note already exists for this document
        const response = await DocumentNotesApi.getNotesByDocument(documentId, user.userId);
        
        if (response.content && response.content.length > 0) {
          // Note exists, load its content
          const note = response.content[0];
          setNoteDocument(note);
          
          // Load note content
          const contentResponse = await DocumentNotesApi.getNoteContent(note.id);
          setNoteContent(contentResponse.content || '');
        } else {
          // No note exists yet, will be created on first edit
          setNoteDocument(null);
          setNoteContent('');
        }
      } catch (err) {
        console.error('Failed to load note:', err);
        setError('Failed to load note. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadNote();
  }, [documentId, user?.userId]);

  // Create note on first content change
  const handleFirstEdit = async (content) => {
    if (noteDocument || isCreating || !content.trim()) return;

    setIsCreating(true);
    setError(null);

    try {
      const noteData = {
        title: `Notes for ${documentName}`,
        description: `Notes linked to document: ${documentName}`,
        content: content,
        parentDocumentId: documentId
      };

      console.log('[DocumentNotes] Creating note with data:', noteData);
      const createdNote = await DocumentNotesApi.createNote(noteData);
      console.log('[DocumentNotes] Note created successfully:', createdNote);
      setNoteDocument(createdNote);
      setNoteContent(content);
      
      // Notify other components that documents have changed
      window.dispatchEvent(new CustomEvent('documents:changed'));
    } catch (err) {
      console.error('[DocumentNotes] Failed to create note:', err);
      setError('Failed to create note. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  // Handle note deletion
  const handleDelete = () => {
    setNoteDocument(null);
    setNoteContent('');
    // Parent component will handle navigation if needed
  };

  if (loading) {
    return (
      <div className="document-notes document-notes--loading">
        <div className="spinner"></div>
        <p>Loading notes...</p>
      </div>
    );
  }

  if (error && !noteDocument) {
    return (
      <div className="document-notes document-notes--error">
        <p>{error}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  // Always show the note editor
  return (
    <div className="document-notes">
      {isCreating ? (
        <div className="document-notes document-notes--creating">
          <div className="spinner"></div>
          <p>Creating note...</p>
        </div>
      ) : (
        <NoteEditor
          noteId={noteDocument?.id || null}
          initialContent={noteContent}
          onSave={noteDocument ? undefined : handleFirstEdit}
          onDelete={noteDocument ? handleDelete : undefined}
          readOnly={false}
        />
      )}
    </div>
  );
};

export default DocumentNotes;
